import { CrossFrameUtils } from '@utils/CrossFrameUtils';

export interface TaskWorkflowConfig {
    taskId: string;
    taskType: string;
    team: string;
    subTeam?: string;
    category?: string;
    stepId?: string;
    idleTimeoutMs?: number;
}

export interface TaskWorkflowEvent {
    taskId: string;
    taskType: string;
    team: string;
    subTeam?: string;
    category?: string;
    stepId?: string;
    inProgress: boolean;
    isIdleTimeout?: boolean;
}

const DEFAULT_IDLE_TIMEOUT_MS = 20 * 60 * 1000;
const IDLE_EVENTS = ['click', 'touchstart', 'keydown', 'scan'];
const TASK_WORKFLOW_METHOD = 'taskWorkflowStateChange';

/**
 * Factory that returns lifecycle callbacks for task workflow event emission.
 * All cross-frame communication, idle detection, and timer management is encapsulated internally.
 *
 * Tools call start(), updateStep(), complete(), and destroy() at the appropriate lifecycle moments.
 * ATBase receives events via CrossFrameUtils and stores state in Redux.
 *
 * Task type gating is handled by ATBase's listener (weblab + allowlist), not here.
 * This factory is a dumb emitter — it will emit for any task type passed in.
 */
export function createTaskWorkflow(config: TaskWorkflowConfig) {
    const { taskId, taskType, team, subTeam, category, idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS } = config;

    let completed = false;
    let currentStepId = config.stepId;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;

    function emit(overrides: Record<string, unknown> = {}) {
        if (completed) return;
        CrossFrameUtils.sendMessageToOtherFrame({
            frame: window.parent,
            message: {
                method: TASK_WORKFLOW_METHOD,
                data: { taskId, taskType, team, subTeam, category, stepId: currentStepId, inProgress: true, ...overrides },
            },
        });
    }

    function startIdleTimer() {
        idleTimer = setTimeout(() => {
            if (!completed) {
                emit({ isIdleTimeout: true });
                startIdleTimer();
            }
        }, idleTimeoutMs);
    }

    function stopIdleTimer() {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = null;
    }

    function resetIdleTimer() {
        stopIdleTimer();
        startIdleTimer();
    }

    function attachIdleListeners() {
        IDLE_EVENTS.forEach((e) => document.addEventListener(e, resetIdleTimer));
    }

    function detachIdleListeners() {
        IDLE_EVENTS.forEach((e) => document.removeEventListener(e, resetIdleTimer));
    }

    function cleanup() {
        stopIdleTimer();
        detachIdleListeners();
    }

    return {
        start: () => {
            emit();
            startIdleTimer();
            attachIdleListeners();
        },

        updateStep: (stepId: string) => {
            if (stepId === currentStepId) return;
            currentStepId = stepId;
            emit({ stepId });
        },

        complete: () => {
            cleanup();
            emit({ inProgress: false });
            completed = true;
        },

        destroy: () => {
            cleanup();
        },
    };
}
